import { useMemo, useState } from "react";
import {
    Avatar,
    Badge,
    Box,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import { apiUrl } from "../../services/api";
import { isPermite } from "../../utilities/isPermite";
import { useChat } from "./ChatProvider";
import { CHAT_PERM_GRUPO, corAvatar, dataResumida, iniciaisNome, normalizar } from "./chatTypes";

type Props = {
    onNovoGrupo: () => void
    onSelecionar?: () => void
};

export default function ChatListaConversas({ onNovoGrupo, onSelecionar }: Props) {
    const { conversas, contatos, conversaAtivaId, abrirConversa, abrirConversaComUsuario } = useChat();
    const [filtro, setFiltro] = useState("");
    const [mostrandoContatos, setMostrandoContatos] = useState(false);

    const podeCriarGrupo = useMemo(() => isPermite(CHAT_PERM_GRUPO), []);

    const conversasFiltradas = useMemo(() => {
        const busca = normalizar(filtro);
        if (!busca) return conversas;
        return conversas.filter(c => normalizar(c.titulo_conversa).includes(busca));
    }, [conversas, filtro]);

    // Contatos que ainda não têm conversa direta aberta
    const contatosFiltrados = useMemo(() => {
        const busca = normalizar(filtro);
        const jaConversando = new Set(
            conversas.filter(c => c.tipo_conversa === 'direta' && c.id_usuario_destino)
                .map(c => Number(c.id_usuario_destino))
        );
        return contatos
            .filter(u => !jaConversando.has(Number(u.id_usuario)))
            .filter(u => !busca || normalizar(u.nome_usuario).includes(busca));
    }, [contatos, conversas, filtro]);

    async function selecionar(id: number) {
        await abrirConversa(id);
        if (onSelecionar) onSelecionar();
    }

    async function iniciarComUsuario(idUsuario: number) {
        await abrirConversaComUsuario(idUsuario);
        setMostrandoContatos(false);
        if (onSelecionar) onSelecionar();
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', bgcolor: '#fff' }}>
            {/* Busca + ações */}
            <Box sx={{ p: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar conversa..."
                    value={filtro}
                    onChange={e => setFiltro(e.target.value)}
                    InputProps={{
                        sx: { fontSize: 12.5 },
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 17 }} />
                            </InputAdornment>
                        ),
                        endAdornment: filtro ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setFiltro("")}>
                                    <ClearIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
                <Tooltip title="Nova conversa">
                    <IconButton size="small" color={mostrandoContatos ? "primary" : "default"} onClick={() => setMostrandoContatos(p => !p)}>
                        <PersonAddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {podeCriarGrupo && (
                    <Tooltip title="Novo grupo">
                        <IconButton size="small" onClick={onNovoGrupo}>
                            <GroupAddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Divider />

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                }}
            >
                {/* Lista de contatos para iniciar conversa nova */}
                {mostrandoContatos && (
                    <>
                        <Typography sx={{ px: 1.5, pt: 1, fontSize: 10.5, fontWeight: 'bold', color: '#757575' }}>
                            INICIAR CONVERSA
                        </Typography>
                        <List disablePadding>
                            {contatosFiltrados.length === 0 && (
                                <Typography sx={{ px: 1.5, py: 1, fontSize: 12, color: '#9e9e9e' }}>
                                    Nenhum contato disponível
                                </Typography>
                            )}
                            {contatosFiltrados.map(u => (
                                <ListItemButton key={`ct${u.id_usuario}`} onClick={() => iniciarComUsuario(u.id_usuario)} sx={{ py: 0.6 }}>
                                    <Avatar
                                        src={String(u.photo_usuario || '').length > 0 ? `${apiUrl}/imguploaded/${u.photo_usuario}` : ""}
                                        sx={{ width: 30, height: 30, mr: 1, fontSize: 12, bgcolor: corAvatar(u.id_usuario) }}
                                    >
                                        {iniciaisNome(u.nome_usuario)}
                                    </Avatar>
                                    <Typography sx={{ fontSize: 12.5 }}>{u.nome_usuario}</Typography>
                                </ListItemButton>
                            ))}
                        </List>
                        <Divider />
                    </>
                )}

                {conversasFiltradas.length === 0 && !mostrandoContatos && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 12.5, color: '#9e9e9e' }}>
                            {filtro ? `Nenhuma conversa para "${filtro}"` : 'Nenhuma conversa ainda'}
                        </Typography>
                        {!filtro && (
                            <Typography sx={{ fontSize: 11.5, color: '#bdbdbd', mt: 0.5 }}>
                                Use o botão + para falar com um colega
                            </Typography>
                        )}
                    </Box>
                )}

                <List disablePadding>
                    {conversasFiltradas.map(c => (
                        <ListItemButton
                            key={c.id_chat_conversa}
                            selected={c.id_chat_conversa === conversaAtivaId}
                            onClick={() => selecionar(c.id_chat_conversa)}
                            sx={{ py: 0.8, alignItems: 'flex-start' }}
                        >
                            <Badge
                                color="error"
                                badgeContent={Number(c.nao_lidas || 0)}
                                max={99}
                                sx={{ mr: 1.2 }}
                            >
                                <Avatar
                                    src={c.foto_conversa ? `${apiUrl}/imguploaded/${c.foto_conversa}` : ""}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        fontSize: 13,
                                        bgcolor: c.tipo_conversa === 'grupo' ? '#455a64' : corAvatar(c.id_usuario_destino || 0),
                                    }}
                                >
                                    {c.tipo_conversa === 'grupo' ? <GroupIcon fontSize="small" /> : iniciaisNome(c.titulo_conversa)}
                                </Avatar>
                            </Badge>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            fontSize: 12.8,
                                            fontWeight: Number(c.nao_lidas || 0) > 0 ? 'bold' : 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {c.titulo_conversa}
                                    </Typography>
                                    <Typography sx={{ fontSize: 9.5, color: '#9e9e9e', flexShrink: 0 }}>
                                        {dataResumida(c.ultima_mensagem_em)}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 11.5,
                                        color: Number(c.nao_lidas || 0) > 0 ? '#424242' : '#9e9e9e',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {c.ultima_mensagem_texto || 'Sem mensagens'}
                                </Typography>
                            </Box>
                        </ListItemButton>
                    ))}
                </List>
            </Box>
        </Box>
    );
}
